/**
 * The hand-built plan ("Build a plan yourself") and the `details` the projection reads (downsizing rebuild,
 * 21 Oct 2026). One mapping each way, so the ledger, a tested plan seeding it, a saved plan reopening it and
 * the projection all read the same fields.
 */
import type { DzLedger } from './DzBuildPlan.tsx';
import type { DownsizeDetails } from './downsizerScenario.tsx';
import type { ProceedsDeploymentValue } from '../../components/strategy/ProceedsDeploymentFields.tsx';
import { dzSaleFigures } from './dzSaleFigures.ts';

const num = (v: unknown) => Number(v) || 0;

/** What the plan puts to work from the money the sale frees (the property with its duty). */
export function dzDownsizerSale(L: Pick<DzLedger, 'downsizerContribution' | 'partnerDownsizerContribution' | 'giftingAmount' | 'funeralBondPurchase' | 'deployment'>): { placed: number } {
    const d = (L.deployment || {}) as ProceedsDeploymentValue;
    const placed = num(L.downsizerContribution) + num(L.partnerDownsizerContribution) + num(L.giftingAmount) + num(L.funeralBondPurchase)
        + num(d.shares) + num(d.investmentProperty) + num(d.ipStampDuty) + num(d.annuity) + num(d.superContribution) + num(d.partnerSuperContribution) + num(d.investmentBond);
    return { placed };
}

/** The ledger as the projection's `details` (applyDownsizeToFactFind). */
export function dzLedgerToDetails(L: DzLedger): DownsizeDetails {
    const { stampDuty, buyUpShortfall } = dzSaleFigures({ salePrice: L.salePrice, sellingCosts: L.sellingCosts, newHomePrice: L.newHomePurchasePrice, stampDuty: L.stampDuty, sellAndRent: L.sellAndRent });
    return {
        salePrice: num(L.salePrice),
        sellingCosts: num(L.sellingCosts),
        stampDuty,
        newHomeState: L.newHomeState,
        newHomePurchasePrice: L.sellAndRent ? 0 : num(L.newHomePurchasePrice),
        rentInstead: L.sellAndRent,
        rentPerWeek: L.sellAndRent ? num(L.rentPerWeek) : 0,
        downsizerContribution: num(L.downsizerContribution),
        partnerDownsizerContribution: num(L.partnerDownsizerContribution),
        commenceIncomeStream: !!L.commenceIncomeStream,
        giftingAmount: num(L.giftingAmount),
        funeralBondPurchase: num(L.funeralBondPurchase),
        deployment: L.deployment || {},
        buyUpFunding: buyUpShortfall > 0 ? L.buyUpFunding : undefined,
        buyUpCgtOverrides: L.buyUpCgtOverrides as Record<string, number>,
    } as DownsizeDetails;
}

/** A plan's `details` (a tested plan, or a saved hand-built one) as the ledger, over a fallback for what it leaves out. */
export function dzLedgerFromDetails(details: Record<string, unknown> | null | undefined, fallback: DzLedger): DzLedger {
    const d = details || {};
    const has = (k: string) => d[k] != null && d[k] !== '';
    const rents = d.rentInstead === true;
    return {
        salePrice: has('salePrice') ? num(d.salePrice) : fallback.salePrice,
        sellingCosts: has('sellingCosts') ? num(d.sellingCosts) : fallback.sellingCosts,
        sellAndRent: rents,
        rentPerWeek: rents ? num(d.rentPerWeek) || fallback.rentPerWeek : fallback.rentPerWeek,
        newHomePurchasePrice: rents ? fallback.newHomePurchasePrice : (has('newHomePurchasePrice') ? num(d.newHomePurchasePrice) : fallback.newHomePurchasePrice),
        stampDuty: has('stampDuty') ? num(d.stampDuty) : fallback.stampDuty,
        newHomeState: typeof d.newHomeState === 'string' ? d.newHomeState : fallback.newHomeState,
        downsizerContribution: num(d.downsizerContribution),
        partnerDownsizerContribution: num(d.partnerDownsizerContribution),
        commenceIncomeStream: d.commenceIncomeStream === true,
        giftingAmount: num(d.giftingAmount),
        funeralBondPurchase: num(d.funeralBondPurchase),
        deployment: { ...((d.deployment || {}) as ProceedsDeploymentValue) },
        buyUpFunding: { ...((d.buyUpFunding || {}) as Record<string, number>) },
        buyUpCgtOverrides: { ...((d.buyUpCgtOverrides || {}) as Record<string, number | null>) },
    };
}

/** "A blank plan": the same sale and home, nothing put to work — every amount the client's to choose. */
export function dzBlankLedger(L: DzLedger): DzLedger {
    return { ...L, downsizerContribution: 0, partnerDownsizerContribution: 0, commenceIncomeStream: false, giftingAmount: 0, funeralBondPurchase: 0, deployment: {} };
}
